// Iteration 088 -- Offers section for school detail page. Shows offers
// targeted at schools (target = school | school_and_public) with an inline
// claim CTA.
import { useEffect, useState } from "react";
import type { SponsorOfferPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { ClaimOfferDialog } from "@/components/claim-offer-dialog";

interface SchoolOffersSectionProps {
  schoolId: string;
}

export function SchoolOffersSection({ schoolId }: SchoolOffersSectionProps) {
  const [offers, setOffers] = useState<SponsorOfferPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimTarget, setClaimTarget] = useState<SponsorOfferPublic | null>(null);

  useEffect(() => {
    setLoading(true);
    api
      .getSchoolOffers(schoolId)
      .then((r) => setOffers(r.offers))
      .catch(() => setOffers([]))
      .finally(() => setLoading(false));
  }, [schoolId]);

  if (loading) return null;
  if (offers.length === 0) return null;

  return (
    <Card data-region="school-offers">
      <CardHeader>
        <CardTitle className="text-base">Sponsor Offers</CardTitle>
        <p className="text-xs text-muted-foreground">
          Discounts and partnerships available to this school.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {offers.map((o) => (
            <div
              key={o.id}
              className="rounded-md border p-3"
              data-item="offer"
              data-item-id={o.id}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-medium text-sm">{o.title}</p>
                  <p className="text-xs text-muted-foreground">{o.sponsor_name}</p>
                </div>
                {o.discount_value && (
                  <span className="shrink-0 rounded bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                    {o.discount_value}
                  </span>
                )}
              </div>
              <p className="mt-2 text-xs line-clamp-2">{o.description}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => setClaimTarget(o)}
              >
                {o.call_to_action_label || "Claim"}
              </Button>
            </div>
          ))}
        </div>
      </CardContent>

      {claimTarget && (
        <ClaimOfferDialog
          offer={claimTarget}
          schoolId={schoolId}
          onClose={() => setClaimTarget(null)}
          onClaimed={() => setClaimTarget(null)}
        />
      )}
    </Card>
  );
}
